/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ServiceInstance } from "app/models/service-instance";
import { GetOrderInformationRequestDto, KissService, ValidationInfo, ValidationState } from "app/services/kiss.service";

@Component({
    templateUrl: './kiss-portal-validation.enter-info.component.html',
    styleUrls: ['./kiss-portal-validation.enter-info.component.css']
  })
export class KissPortalValidationEnterInfoComponent implements OnInit {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  portal: ServiceInstance;

  orderNumber: string = '';
  transportId: string = '';
  billOfLading: string = ''
  vendorInvoiceNumber: string = '';
  inboundDelivery: string = '';

  isUiDisabled = false;

  /** Constructor */
  constructor(
    private kissService: KissService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  /**
   * Initializes the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnInit(): void {
    this.route.data.subscribe(
      (data: {
        portal: ServiceInstance
      }) => {
        console.log(`Obtained Portal: '${data?.portal?.id}/${data?.portal?.name}'`);
        this.portal = data?.portal;
      });
  }

  /**
   * Called when the "Get Info" Button is clicked in the UI.
   * Calls the Server to get the "Order Information" for the information
   * entered in the UI. 
   */
  onGetInfoClicked(): void {
    if (!this.isUiDisabled) {
      this.isUiDisabled = true;
      this.indefiniteProgressDialog.show("Processing", "Obtaining Order info...");
      this.getInfo();
    }
  }

  async getInfo(): Promise<void> {
    const dto = {
      order_number: this.orderNumber,
      transport_id: this.transportId,
      vendor_invoice: this.vendorInvoiceNumber,
      bill_of_lading: this.billOfLading,
      inbound_delivery: this.inboundDelivery
    } as GetOrderInformationRequestDto;

    let state: ValidationInfo = null;
    try {
      state = await this.kissService.enterOrderInfo(this.portal.id, dto).toPromise();
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.isUiDisabled = false;
      this.handleError(error);
      return;
    }

    this.isUiDisabled = false;
    this.indefiniteProgressDialog.hide();
    console.log(`Obtained State after Enter Info: ${JSON.stringify(state)}`);

    if (state?.state == ValidationState.OrderBeingDisplayed) { 
      this.router.navigate(['kiss', 'portal-validation', this.portal.id, 'dashboard'], {});
    }
  }

  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}
